import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import ExpandableSection from '@cloudscape-design/components/expandable-section';

export const AboutMailEmlAnalyzerTab = () => {
  return (
    <SpaceBetween size="l">
      {/* Overview */}
      <Container
        header={
          <Header variant="h2">
            Check EMail EML File on invisible Characters
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            This tool analyzes email files in EML format with a primary focus on detecting and analyzing invisible characters 
            that may be hidden in the email content. Invisible characters can be used for tracking, fingerprinting, phishing attacks, 
            or bypassing security filters. The tool also performs comprehensive email header analysis to verify authentication and trace delivery paths.
          </Box>

          <Alert type="info">
            <SpaceBetween size="xs">
              <Box variant="p">
                <strong>Privacy Guaranteed:</strong> All email parsing and analysis happens locally in your browser. 
                No email content is ever sent to any server or third party. Your emails remain completely private.
              </Box>
            </SpaceBetween>
          </Alert>
        </SpaceBetween>
      </Container>

      {/* Email Format */}
      <Container
        header={
          <Header variant="h2">
            Email Format
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ExpandableSection headerText="EML Format" variant="container" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                <strong>EML</strong> is a standard email format following RFC 822 and RFC 2822 specifications. 
                It stores email messages in plain text format with MIME encoding for attachments and rich content.
              </Box>
              <Box variant="p">
                <strong>Key Features:</strong>
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li>Plain text format, human-readable</li>
                <li>Contains complete email headers and metadata</li>
                <li>Supports MIME multipart messages (HTML, attachments)</li>
                <li>Used by most email clients (Thunderbird, Apple Mail, Outlook)</li>
                <li>Can be opened with any text editor</li>
              </ul>
              <Box variant="p">
                <strong>How to export EML files:</strong>
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>Outlook:</strong> Drag email to desktop or use "Save As" with a third-party add-in</li>
                <li><strong>Gmail:</strong> Open email → Three dots menu → Download message</li>
                <li><strong>Apple Mail:</strong> File → Save As → Choose format "Raw Message Source"</li>
                <li><strong>Thunderbird:</strong> File → Save As → File</li>
              </ul>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      {/* Analysis Features */}
      <Container
        header={
          <Header variant="h2">
            What Analysis is Performed
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ExpandableSection headerText="1. Email Metadata Extraction" variant="container" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                Extracts and displays basic email information:
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>From:</strong> Sender's email address</li>
                <li><strong>To:</strong> Recipient email addresses</li>
                <li><strong>CC/BCC:</strong> Carbon copy recipients (if present)</li>
                <li><strong>Subject:</strong> Email subject line</li>
                <li><strong>Date:</strong> When the email was sent</li>
                <li><strong>Attachments:</strong> List of attached files with names, types, and sizes</li>
              </ul>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="2. Mail Header Analysis" variant="container" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                Performs comprehensive analysis of email headers, including:
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>SPF (Sender Policy Framework):</strong> Verifies sender's IP address is authorized</li>
                <li><strong>DKIM (DomainKeys Identified Mail):</strong> Validates email signature and authenticity</li>
                <li><strong>DMARC (Domain-based Message Authentication):</strong> Checks domain-level email authentication</li>
                <li><strong>Delivery Path:</strong> Traces the email's route through mail servers</li>
                <li><strong>Relay Hops:</strong> Identifies intermediate mail servers</li>
                <li><strong>Security Issues:</strong> Detects authentication failures, suspicious headers, and potential spoofing</li>
              </ul>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="3. Body Content Analysis" variant="container" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                Extracts and displays email body content in multiple formats:
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>Plain Text:</strong> Raw text content of the email</li>
                <li><strong>HTML:</strong> HTML source code if the email contains rich formatting</li>
                <li><strong>Text Extraction:</strong> Extracts readable text from HTML for analysis</li>
              </ul>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="4. Invisible Character Detection (PRIMARY FOCUS)" variant="container" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                <strong>This is the core feature of the tool.</strong> Performs comprehensive analysis of invisible Unicode characters in email content:
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>Zero-Width Characters:</strong> ZWSP (U+200B), ZWNJ (U+200C), ZWJ (U+200D), Word Joiner (U+2060)</li>
                <li><strong>Directional Marks:</strong> LRM (U+200E), RLM (U+200F), BiDi control characters</li>
                <li><strong>Format Characters:</strong> Soft hyphens, format controls, invisible separators</li>
                <li><strong>Steganography Detection:</strong> Hidden data patterns in invisible character sequences</li>
                <li><strong>Normalization Attacks:</strong> Unicode normalization instability</li>
                <li><strong>Homoglyphs:</strong> Look-alike characters for phishing (e.g., Cyrillic 'а' vs Latin 'a')</li>
                <li><strong>BiDi Exploits:</strong> Text direction manipulation for URL/domain spoofing</li>
                <li><strong>Script Mixing:</strong> Unusual mixing of character scripts</li>
                <li><strong>Character Width Abuse:</strong> Full-width vs half-width character substitution</li>
                <li><strong>Mathematical Symbols:</strong> Math Unicode symbols substituting normal characters</li>
                <li><strong>Control Characters:</strong> ANSI escapes, terminal controls, backspace exploitation</li>
              </ul>
              <Box variant="p">
                <strong>Analysis is performed on:</strong>
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>Email Subject:</strong> Full invisible character analysis with 21+ security pattern checks</li>
                <li><strong>Plain Text Body:</strong> Complete analysis with line-by-line character tracking</li>
                <li><strong>HTML Body:</strong> Extracted text analyzed for invisible characters and patterns</li>
                <li><strong>URLs Found in Email:</strong> Each URL is scanned for invisible characters</li>
              </ul>
              <Box variant="p">
                <strong>Each section provides:</strong>
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li>Visual highlighting of invisible characters in the text</li>
                <li>Exact position tracking (line and column numbers)</li>
                <li>Character-by-character details with Unicode codes</li>
                <li>Security risk assessment with threat level scoring</li>
                <li>Interactive position highlighting when clicking analysis results</li>
              </ul>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="5. URL Security Analysis" variant="container">
            <SpaceBetween size="s">
              <Box variant="p">
                Automatically extracts and analyzes all URLs found in the email:
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>Microsoft 365 Safe Links:</strong> Automatically decodes M365 wrapped URLs</li>
                <li><strong>Invisible Character Detection:</strong> Scans each URL for hidden Unicode characters</li>
                <li><strong>Security Scoring:</strong> Risk level assessment (Safe/Suspicious/Dangerous)</li>
                <li><strong>Grouped Display:</strong> URLs organized by risk level for easy review</li>
              </ul>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      {/* Security Considerations */}
      <Container
        header={
          <Header variant="h2">
            Security Considerations
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning" header="Important Security Notes">
            <SpaceBetween size="xs">
              <Box variant="p">
                When analyzing email files, keep these security considerations in mind:
              </Box>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>Attachments are NOT executed:</strong> This tool only lists attachment metadata. Attachments are not extracted or executed.</li>
                <li><strong>HTML is displayed as code:</strong> HTML content is shown as source code to prevent execution of malicious scripts.</li>
                <li><strong>File size limit:</strong> Maximum file size is 10MB to prevent memory issues in your browser.</li>
                <li><strong>Local processing only:</strong> All analysis happens in your browser. No data leaves your device.</li>
                <li><strong>Links are not clickable:</strong> URLs in emails are displayed as text to prevent accidental navigation to malicious sites.</li>
              </ul>
            </SpaceBetween>
          </Alert>

          <Box variant="h3">Use Cases</Box>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li><strong>AI Content Detection:</strong> Identify invisible character fingerprints in emails potentially generated or processed by AI tools</li>
            <li><strong>Phishing Detection:</strong> Detect invisible characters used to disguise malicious URLs or bypass security filters</li>
            <li><strong>Tracking Detection:</strong> Uncover hidden tracking mechanisms using zero-width characters</li>
            <li><strong>Email Forensics:</strong> Investigate email headers and content for steganography and hidden data</li>
            <li><strong>Security Auditing:</strong> Comprehensive analysis of invisible characters and authentication (SPF, DKIM, DMARC)</li>
            <li><strong>Spam Analysis:</strong> Identify BiDi manipulation, homoglyphs, and other invisible character techniques</li>
            <li><strong>Compliance Verification:</strong> Check email authentication for compliance requirements</li>
          </ul>
        </SpaceBetween>
      </Container>

      {/* Technical Details */}
      <Container
        header={
          <Header variant="h2">
            Technical Details
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="h3">File Format Support</Box>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li><strong>EML:</strong> Fully supported with RFC 822/2822 compliance</li>
            <li><strong>MIME multipart:</strong> Handles multipart messages with HTML and attachments</li>
            <li><strong>Header encoding:</strong> Decodes RFC 2047 encoded headers (Base64, Quoted-Printable)</li>
            <li><strong>Character sets:</strong> Supports UTF-8 and common character encodings</li>
          </ul>

          <Box variant="h3">Parsing Capabilities</Box>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li>Header extraction with multi-line (folded) header support</li>
            <li>MIME boundary detection and parsing</li>
            <li>Content-Transfer-Encoding handling (Base64, Quoted-Printable, 7bit, 8bit)</li>
            <li>HTML entity decoding and text extraction</li>
            <li>Attachment metadata extraction (filename, content-type, size)</li>
          </ul>

          <Box variant="h3">Browser Compatibility</Box>
          <Box variant="p">
            This tool uses modern browser APIs and requires:
          </Box>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li>FileReader API (for file upload)</li>
            <li>ES6+ JavaScript support</li>
            <li>Modern browser (Chrome 90+, Firefox 88+, Safari 14+, Edge 90+)</li>
          </ul>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

